import React, {useCallback, useEffect, useState} from 'react';
import {Heading} from '@albert-io/atomic';
import {callTargetedAction, setUpStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';

import FreeResponseStore from '../FreeResponseQuestion.store';
import freeResponseActions from '../FreeResponseQuestion.actions';
import {WSStore} from '../FreeResponseQuestion.types';

import WrittenSubmissionContext from './WrittenSubmission.context';
import WrittenSubmissionInput from './Editor/WrittenSubmissionInput.react';

import BottomBar from './BottomBar.react';

interface Props {
  storeName: string;
  submitted?: boolean;
  question: any;
}

const WrittenStudentSubmission = ({storeName, submitted, question}: Props) => {
  const [store, setStore] = useState<null | WSStore>(null);
  const getWordCount = useCallback(() => {
    if (!store) return 0;
    return store.getWordCount();
  }, [store]);

  const onChange = useCallback(
    (data) => {
      if (!submitted) {
        callTargetedAction({
          name: freeResponseActions.UPDATE_RESPONSE,
          payload: data,
          targetStore: storeName
        });
      }
    },
    [storeName, submitted]
  );

  const startTimer = useCallback(() => {
    if (!submitted) {
      callTargetedAction({
        name: freeResponseActions.START_TIMER,
        targetStore: storeName
      });
    }
  }, [storeName, submitted]);

  const saveEditorContent = useCallback(() => {
    if (!submitted) {
      callTargetedAction({
        name: freeResponseActions.SAVE_DRAFT,
        targetStore: storeName
      });
    }
  }, [storeName, submitted]);

  const loadEditorContent = useCallback(() => {
    if (!submitted) {
      callTargetedAction({
        name: freeResponseActions.LOAD_DRAFT,
        targetStore: storeName
      });
    }
  }, [storeName, submitted]);

  const cancelDebounce = useCallback(() => {
    if (!submitted) {
      callTargetedAction({
        name: freeResponseActions.CANCEL_AUTO_SAVE,
        targetStore: storeName
      });
    }
  }, [storeName, submitted]);

  useEffect(() => {
    const newStore = setUpStore(FreeResponseStore, storeName, question.getId(), question);
    setStore(null);

    const timeout = setTimeout(() => {
      // hot swapping written submission input editor values causes a bug where if the previous cursor is out of bounds of the next
      // written submission, it causes an error. This only occurs when the next question is another written submission
      startTimer(); // start time_elapsed timer
      setStore(newStore);
    }, 100);

    loadEditorContent();

    return () => {
      saveEditorContent();
      clearTimeout(timeout);
      cancelDebounce();
    };
  }, [storeName, saveEditorContent, loadEditorContent, question, cancelDebounce, startTimer]);

  useEffect(() => {
    const warnUser = (e: BeforeUnloadEvent) => {
      if (!submitted && store && store.getHasUnsavedEdits() && !sessionStore.hasValidSession()) {
        e.preventDefault();
        e.returnValue = 'Leaving this page may result in unsaved changes';
      }
    };
    window.addEventListener('beforeunload', warnUser);

    return () => {
      window.removeEventListener('beforeunload', warnUser);
    };
  }, [saveEditorContent, submitted, store]);

  const editorState = store ? store.getValue() : null;
  const labelText = submitted ? 'Your Response' : 'Provide Your Response Below';

  return (
    <div className='u-mar-b_4 u-width_100pc'>
      <Heading as='h2' size='s' id='wss-text-input'>
        {labelText}
      </Heading>

      {store && editorState && (
        <WrittenSubmissionContext.Provider value={{store, storeName, submitted}}>
          <WrittenSubmissionInput
            onChange={onChange}
            value={editorState}
            readOnly={submitted}
            aria-labelledby='wss-text-input'
          />
          {!submitted && <BottomBar store={store} wordCount={getWordCount()} />}
        </WrittenSubmissionContext.Provider>
      )}
    </div>
  );
};

export default WrittenStudentSubmission;
